/**
 * Identity + user-management API client (real auth — never mocked, §9.7/§13.7).
 *
 * Sessions live in httpOnly cookies, so every request uses `credentials: include`.
 * Mutations must echo the readable CSRF cookie in `X-CSRF-Token` (double submit).
 */
import type {
  AccountProof,
  AccountSecurity,
  ActivityEvent,
  AuthUser,
  InviteResult,
  LoginResult,
  ManagedUser,
  PermissionDefinition,
  RoleKey,
  TwoFactorSetup,
  AccountProfile,
} from '../types/identity'

export const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000').replace(
  /\/+$/,
  '',
)

export const API_V1_BASE = '/api/v1'
const AUTH_BASE = `${API_V1_BASE}/auth`

type QueryValue =
  | string
  | number
  | boolean
  | undefined
  | Array<string | number>

/** Absolute when an API base is configured, otherwise same-origin (behind a proxy). */
function buildUrl(
  path: string,
  query?: Record<string, QueryValue>,
  base: string = AUTH_BASE,
): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item))
    } else {
      params.set(key, String(value))
    }
  }
  const queryString = params.toString()

  if (!API_BASE_URL) {
    return `${base}${path}${queryString ? `?${queryString}` : ''}`
  }

  const url = new URL(`${API_BASE_URL}${base}${path}`)
  url.search = queryString
  return url.toString()
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly violations: string[]

  constructor(status: number, code: string, message: string, violations: string[] = []) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.violations = violations
  }

  get isTwoFactorRequired(): boolean {
    return this.code === '2fa_required'
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, QueryValue>
  /** Abort the HTTP request when the caller (e.g. TanStack Query) cancels it. */
  signal?: AbortSignal
}

/** Paths that are part of the sign-in flow itself — refreshing inside them would loop. */
const NO_SILENT_REFRESH = ['/login', '/refresh', '/logout', '/password/', '/invites/accept']

/**
 * One shared refresh, however many requests notice the expired access token at once.
 *
 * This must be single-flight: rotating the refresh token twice in parallel looks exactly
 * like a stolen token, and the server revokes every session when it sees that. That is a
 * hard logout, so the retry is shared rather than per-call.
 */
let refreshing: Promise<boolean> | null = null

async function refreshSilently(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      await request<LoginResult>('/refresh', { method: 'POST' }, false)
      return true
    } catch {
      return false
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
  allowRefresh = true,
  base = AUTH_BASE,
): Promise<T> {
  const url = buildUrl(path, options.query, base)

  const method = options.method ?? 'GET'
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'

  if (method !== 'GET') {
    const csrf = readCookie('ssos_csrf')
    if (csrf) headers['X-CSRF-Token'] = csrf
  }

  let response: Response
  try {
    response = await fetch(url, {
      method,
      headers,
      credentials: 'include',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    })
  } catch (error) {
    // An aborted query is not a network failure. Preserve the AbortError so the
    // query layer can treat it as a cancellation instead of painting an error.
    if (options.signal?.aborted || (error as { name?: string } | null)?.name === 'AbortError') {
      throw error
    }
    throw new ApiError(0, 'network_error', 'Cannot reach the Store OS API — is the backend running?')
  }

  if (response.status === 204) return undefined as T

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    // A 401 usually means the 15-minute access token expired — which happens in the middle
    // of ordinary work, not because anyone was signed out. Refresh once and replay the
    // request instead of dropping the person on the login screen (bug 2026-10-17).
    if (
      response.status === 401 &&
      allowRefresh &&
      !NO_SILENT_REFRESH.some((prefix) => path.startsWith(prefix))
    ) {
      if (await refreshSilently()) {
        return request<T>(path, options, false, base)
      }
    }

    const detail = (payload as { detail?: unknown } | null)?.detail
    if (Array.isArray(detail)) {
      // FastAPI validation errors (e.g. a too-short name) arrive as a list. Showing the
      // field and reason is far more useful than the generic “Something went wrong”.
      const messages = detail
        .map((item) => {
          if (!item || typeof item !== 'object') return String(item)
          const problem = item as { loc?: unknown; msg?: unknown }
          const location = Array.isArray(problem.loc)
            ? problem.loc.filter((part) => part !== 'body').join('.')
            : ''
          const message = typeof problem.msg === 'string' ? problem.msg : ''
          return location && message ? `${location}: ${message}` : message
        })
        .filter(Boolean)
      throw new ApiError(
        response.status,
        'validation_error',
        messages.join(' · ') || 'Something went wrong',
      )
    }
    if (detail && typeof detail === 'object') {
      const problem = detail as { code?: string; message?: string; violations?: string[] }
      throw new ApiError(
        response.status,
        problem.code ?? 'error',
        problem.message ?? 'Something went wrong',
        problem.violations ?? [],
      )
    }
    throw new ApiError(
      response.status,
      'error',
      typeof detail === 'string' ? detail : 'Something went wrong',
    )
  }

  return payload as T
}

/**
 * Authenticated GET against another v1 module (Butlaroo, grid, overview …).
 *
 * Reuses the identity client's cookie session, single-flight refresh and error
 * envelope, so a module never needs its own fetch wrapper.
 */
export function apiGet<T>(
  path: string,
  query?: RequestOptions['query'],
  signal?: AbortSignal,
): Promise<T> {
  return request<T>(path, { query, signal }, true, API_V1_BASE)
}

/* ------------------------------------------------------------------ session */
export function login(
  email: string,
  password: string,
  rememberDevice = false,
): Promise<LoginResult> {
  return request<LoginResult>('/login', {
    method: 'POST',
    body: { email, password, remember_device: rememberDevice },
  })
}

export function loginWithTwoFactor(
  ticket: string,
  values: { code?: string; recovery_code?: string },
  rememberDevice = false,
): Promise<LoginResult> {
  return request<LoginResult>('/login/2fa', {
    method: 'POST',
    body: { ticket, code: values.code ?? null, recovery_code: values.recovery_code ?? null },
    query: { remember_device: rememberDevice ? true : undefined },
  })
}

export function logout(): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/logout', { method: 'POST' })
}

export function fetchMe(): Promise<AuthUser> {
  return request<AuthUser>('/me')
}

/** The backend's standardized permission catalogue (for future role editing). */
export function fetchPermissions(): Promise<PermissionDefinition[]> {
  return request<PermissionDefinition[]>('/permissions')
}

export function forgotPassword(email: string): Promise<{ ok: boolean; detail?: string }> {
  return request('/password/forgot', { method: 'POST', body: { email } })
}

export function resetPassword(token: string, newPassword: string): Promise<{ ok: boolean }> {
  return request('/password/reset', {
    method: 'POST',
    body: { token, new_password: newPassword },
  })
}

export function acceptInvite(
  token: string,
  fullName: string,
  password: string,
  rememberDevice = false,
): Promise<LoginResult> {
  return request<LoginResult>('/invites/accept', {
    method: 'POST',
    body: { token, full_name: fullName, password },
    query: { remember_device: rememberDevice ? true : undefined },
  })
}

/* ---------------------------------------------------------------------- 2FA */
export function verifyEmail(token: string): Promise<{ ok: boolean }> {
  return request('/email/verify', { method: 'POST', body: { token } })
}

let setupRequest: Promise<TwoFactorSetup> | null = null

export function startTwoFactorSetup(): Promise<TwoFactorSetup> {
  // React StrictMode runs the enrolment effect twice in development. Both mounts asking
  // for a secret at the same time used to race the unique row and turn the second call into
  // a 500, leaving the gate on “Requesting a fresh secret…”. The server is now race-safe
  // too, but sharing one in-flight request keeps the client from creating the race at all.
  setupRequest ??= request<TwoFactorSetup>('/2fa/setup', { method: 'POST' }).finally(() => {
    setupRequest = null
  })
  return setupRequest
}

export function confirmTwoFactor(code: string): Promise<{ recovery_codes: string[] }> {
  return request('/2fa/confirm', { method: 'POST', body: { code } })
}

/** One day's opening window from the store master data. */
export interface OpeningHour {
  weekday: string
  /** Local 24-hour time, e.g. "11:00". */
  opens: string
  /** Local 24-hour time; when <= opens the shop closes on the next day. */
  closes: string
  closes_next_day: boolean
}

/** Store master data (D-40) — numbers, codes, names and opening hours. */
export interface StoreOption {
  id: number
  code: string
  name: string | null
  city: string | null
  label: string
  is_active: boolean
  /** Monday-first weekly schedule; null when the shop's hours are not known. */
  opening_hours: OpeningHour[] | null
  open_hours_known: boolean
  /** Server-side open/closed state; null when the hours are unknown. */
  is_open_now: boolean | null
}

export function fetchStores(): Promise<StoreOption[]> {
  return request<StoreOption[]>('/stores')
}

/* ------------------------------------------------- self-service account settings */
export function fetchAccountSecurity(): Promise<AccountSecurity> {
  return request<AccountSecurity>('/account/security')
}

/** My own name, nickname and picture (D-44). */
export function fetchAccountProfile(): Promise<AccountProfile> {
  return request<AccountProfile>('/account/profile')
}

/**
 * Partial profile update: only the fields you send are touched, and `null` clears the
 * nickname/picture. No password or code is asked for — this is not a sensitive change.
 */
export function updateAccountProfile(payload: {
  full_name?: string
  nickname?: string | null
  avatar?: string | null
}): Promise<AccountProfile> {
  return request<AccountProfile>('/account/profile', { method: 'POST', body: payload })
}

export function sendAccountCode(): Promise<{ ok: boolean; detail?: string }> {
  return request('/account/code', { method: 'POST' })
}

export function changeOwnPassword(
  payload: AccountProof & { new_password: string },
): Promise<{ ok: boolean; detail?: string }> {
  return request('/account/password', { method: 'POST', body: payload })
}

export function changeOwnEmail(
  payload: AccountProof & { new_email: string },
): Promise<{ ok: boolean; detail?: string }> {
  return request('/account/email', { method: 'POST', body: payload })
}

export function rotateOwnTwoFactor(payload: AccountProof): Promise<TwoFactorSetup> {
  return request<TwoFactorSetup>('/account/2fa/rotate', { method: 'POST', body: payload })
}

export function disableOwnTwoFactor(payload: AccountProof): Promise<{ ok: boolean; detail?: string }> {
  return request('/account/2fa/disable', { method: 'POST', body: payload })
}

/* ------------------------------------------------------- user management */
export function listUsers(
  params: { role?: string; search?: string; includeInactive?: boolean } = {},
): Promise<ManagedUser[]> {
  return request<ManagedUser[]>('/users', {
    query: {
      role: params.role,
      search: params.search,
      include_inactive: params.includeInactive,
    },
  })
}

export function inviteUser(payload: {
  email: string
  /** Optional: the invitee sets their real name when they accept. */
  full_name?: string
  role: RoleKey
  /** One shop for store roles. */
  store_id: number | null
  /** A *selection* of shops — district managers only (D-42). */
  scope_store_ids?: number[]
}): Promise<InviteResult> {
  return request<InviteResult>('/invites', { method: 'POST', body: payload })
}

export function changeUserEmail(userId: string, newEmail: string): Promise<ManagedUser> {
  return request<ManagedUser>(`/users/${userId}/email`, {
    method: 'POST',
    body: { new_email: newEmail },
  })
}

export function sendUserPasswordReset(userId: string): Promise<{ ok: boolean; detail?: string }> {
  return request(`/users/${userId}/password-reset`, { method: 'POST' })
}

export function resetUserTwoFactor(userId: string): Promise<{ ok: boolean; detail?: string }> {
  return request(`/users/${userId}/2fa/reset`, { method: 'POST' })
}

export function updateUserAccess(
  userId: string,
  payload: {
    role: RoleKey
    store_id: number | null
    scope_store_ids?: number[] | null
    permission_grants?: string[]
    permission_revocations?: string[]
  },
): Promise<ManagedUser> {
  return request<ManagedUser>(`/users/${userId}/access`, {
    method: 'PATCH',
    body: payload,
  })
}

export function setUserActivation(userId: string, isActive: boolean): Promise<ManagedUser> {
  return request<ManagedUser>(`/users/${userId}/activation`, {
    method: 'POST',
    body: { is_active: isActive },
  })
}

/** Permanent deletion — system administrator only (D-41). */
export function deleteUser(
  userId: string,
  reason?: string,
): Promise<{ ok: boolean; deleted: { id: string; email: string; full_name: string; role: RoleKey } }> {
  return request(`/users/${userId}`, { method: 'DELETE', query: { reason } })
}

export function fetchActivity(
  params: { userId?: string; event?: string; limit?: number } = {},
): Promise<ActivityEvent[]> {
  return request<ActivityEvent[]>('/activity', {
    query: { user_id: params.userId, event: params.event, limit: params.limit },
  })
}

